import './TestOrderAttachmentComponent.scss'
import React, {useCallback, useEffect, useRef, useState} from 'react';
import FormTitleWithNavigationBack
    from '../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack';
import ButtonComponent from '../../../../shared/components/core/button/ButtonComponent';
import {CommonService} from '../../../../shared/services';
import LoaderComponent from "../../../../shared/components/loader/loaderComponent";
import FeedbackComponent from "../../../../shared/components/feedback/feedbackComponent";
import InputComponent from '../../../../shared/components/core/input/InputComponent';
import _ from "lodash";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import AttachmentViewDownloadComponent
    from '../../../../shared/components/core/attachment-view-download/attachmentViewDownloadComponent';
import StatusCardComponent from "../../../../shared/components/status-card/statusCardComponent";
import {ImageConfig} from "../../../../constants";
import FilePreviewerComponent from "../../../../shared/components/file-previewer/filePreviewerComponent";
import DialogComponent from "../../../../shared/components/dialog/dialogComponent";
import PdfPreviewComponent from "../../../../shared/components/pdf-preview/pdfPreviewComponent";
import ErrorComponent from "../../../../shared/components/form-controls/error/errorComponent";
import Grid from "@mui/material/Grid";
import DeleteIcon from "@mui/icons-material/Delete";

interface TestOrderAttachmentComponentProps {
    next: Function;
    prev: Function;
    cancel: Function;
    testOrderID: number;
    testOrderDetails: any;
    onDelete: Function;
    isDeleteLoading: boolean;
    canDiscardTestOrder: boolean;
}

const TestOrderAttachmentComponent = (props: TestOrderAttachmentComponentProps) => {
    const {next, prev, cancel, testOrderID, onDelete, isDeleteLoading, canDiscardTestOrder} = props;

    const [orderAttachmentList, setOrderAttachmentList] = useState<any[]>([]);
    const [isAttachmentListLoading, setIsAttachmentListLoading] = useState<boolean>(false);
    const [isAttachmentListLoaded, setIsAttachmentListLoaded] = useState<boolean>(false);
    const [isAttachmentListLoadingFailed, setIsAttachmentListLoadingFailed] = useState<boolean>(false);

    const [patientAttachmentList, setPatientAttachmentList] = useState<any[]>([]);

    const [orderAttachmentFile, setOrderAttachmentFile] = useState<any>(undefined);
    const [orderAttachmentTitle, setOrderAttachmentTitle] = useState<string>("");

    const [patientAttachmentFile, setPatientAttachmentFile] = useState<File | undefined>(undefined);
    const [patientAttachmentTitle, setPatientAttachmentTitle] = useState<string>("");

    const patientAttachmentFileRef = useRef<HTMLInputElement | null>(null);
    const orderAttachmentFileRef = useRef<HTMLInputElement | null>(null);
    const [showPatientAttachmentPreviewDialog, setShowPatientAttachmentPreviewDialog] = useState<boolean>(false);
    const [showOrderAttachmentPreviewDialog, setShowOrderAttachmentPreviewDialog] = useState<boolean>(false);
    const [isNewPatientAttachment, setNewPatientAttachment] = useState<boolean>(false)
    const [isNewOrderAttachment, setNewOrderAttachment] = useState<boolean>(false)
    const [isContinueVisible, setIsContinueVisible] = useState<boolean>(false);

    const [isOrderAttachmentBeingUploaded, setIsOrderAttachmentBeingUploaded] = useState<boolean>(false);
    const [isPatientAttachmentBeingUploaded, setIsPatientAttachmentBeingUploaded] = useState<boolean>(false);


    const getAttachmentList = useCallback(() => {
        const payload = {};
        setIsAttachmentListLoading(true);
        setIsAttachmentListLoaded(false);
        setIsAttachmentListLoadingFailed(false);
        let attachmentList: any = {};
        let orderAttachmentList: any = [];
        let patientAttachmentList: any = [];
        CommonService._testOrder.getTestOrderAttachments(testOrderID, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.success) {
                    attachmentList = response?.data;
                    if (Object.keys(attachmentList).length === 0) {
                        attachmentList = [];
                        orderAttachmentList = [];
                        patientAttachmentList = [];
                    } else {
                        orderAttachmentList = attachmentList.order;
                        patientAttachmentList = attachmentList.patient;
                    }
                    setOrderAttachmentList(orderAttachmentList);
                    setPatientAttachmentList(patientAttachmentList);
                }

                setIsAttachmentListLoading(false);
                setIsAttachmentListLoaded(true);
                setIsAttachmentListLoadingFailed(false);
            })
            .catch((error: any) => {
                setOrderAttachmentList(orderAttachmentList);
                setPatientAttachmentList(patientAttachmentList);
                setIsAttachmentListLoading(false);
                setIsAttachmentListLoaded(false);
                setIsAttachmentListLoadingFailed(true);
            });
    }, [testOrderID]);

    const handleOrderAttachmentSuccess = useCallback((response: any) => {
        CommonService._alert.showToast(response?.message || 'Added Order Attachments', "success");
        setOrderAttachmentList((oldState: any) => {
            if (oldState?.length > 0) {
                return [response.data, ...oldState]
            } else {
                return [response.data]
            }
        });
        setIsOrderAttachmentBeingUploaded(false);
        setOrderAttachmentFile(undefined);
        setOrderAttachmentTitle("");
    }, []);

    const handlePatientAttachmentSuccess = useCallback((response: any) => {
        CommonService._alert.showToast(response?.message || 'Added Patient Attachments', "success");
        setPatientAttachmentList((oldState: any) => {
            if (oldState?.length > 0) {
                return [response.data, ...oldState]
            } else {
                return [response.data]
            }
        })
        setIsPatientAttachmentBeingUploaded(false);
        setPatientAttachmentFile(undefined);
        setPatientAttachmentTitle("");
    }, []);

    const onOrderAttachmentSubmit = useCallback(() => {
        const formData = new FormData();
        if (orderAttachmentFile) {
            formData.append('image', orderAttachmentFile);
            formData.append('attachmentType', 'order')
        }
        formData.append('title', orderAttachmentTitle);
        setIsOrderAttachmentBeingUploaded(true);
        CommonService._testOrder.uploadOrderAttachments(testOrderID, formData)
            .then((response: IAPIResponseType<any>) => {
                handleOrderAttachmentSuccess(response);
                setShowOrderAttachmentPreviewDialog(false);
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.message || 'Error uploading order attachment', "error");
                setIsOrderAttachmentBeingUploaded(false);
            })
    }, [orderAttachmentFile, handleOrderAttachmentSuccess, orderAttachmentTitle, testOrderID]);

    const onPatientAttachmentSubmit = useCallback(() => {
        const formData = new FormData();
        if (patientAttachmentFile) {
            formData.append('image', patientAttachmentFile);
            formData.append('attachmentType', 'patient')
        }
        formData.append('title', patientAttachmentTitle);
        setIsPatientAttachmentBeingUploaded(true);
        CommonService._testOrder.uploadPatientAttachments(testOrderID, formData)
            .then((response: IAPIResponseType<any>) => {
                handlePatientAttachmentSuccess(response);
                setShowPatientAttachmentPreviewDialog(false);
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.message || 'Error uploading order attachment', "error");
                setIsPatientAttachmentBeingUploaded(false);
            })
    }, [patientAttachmentFile, handlePatientAttachmentSuccess, patientAttachmentTitle, testOrderID]);

    const removeOrderAttachment = useCallback((attachmentId: any) => {
        const payload = {};
        CommonService._testOrder.deleteTestOrderAttachment(testOrderID, attachmentId, payload)
            .then((response: IAPIResponseType<any>) => {
                CommonService._alert.showToast(response?.message || 'Order attachment has been removed', "success");
                setOrderAttachmentList((oldState: any) => {
                    const updatedList = _.cloneDeep(oldState);
                    const index = updatedList.findIndex((item: any) => item.id === attachmentId);
                    updatedList.splice(index, 1);
                    return updatedList;
                })
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.message || 'Error deleting order attachment', "error");
            })
    }, [testOrderID]);

    const removePatientAttachment = useCallback((attachmentId: any) => {
        const payload = {};
        CommonService._testOrder.deleteTestOrderPatientAttachment(testOrderID, attachmentId, payload)
            .then((response: IAPIResponseType<any>) => {
                CommonService._alert.showToast(response?.message || 'Patient attachment has been removed', "success");
                setPatientAttachmentList((oldState: any) => {
                    const updatedList = _.cloneDeep(oldState);
                    const index = updatedList.findIndex((item: any) => item.id === attachmentId);
                    updatedList.splice(index, 1);
                    return updatedList;
                })
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.message || 'Error deleting patient attachment', "error");
            })
    }, [testOrderID]);


    const handleAttachmentRemove = useCallback((id: any, type: any) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: 'Are you sure you want to delete this attachment?',
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.DeleteImage}`,
            direction: "up",
            yes: {
                text: "Confirm",
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        })
            .then(() => {
                if (type === 'order') {
                    removeOrderAttachment(id);
                } else {
                    removePatientAttachment(id)
                }
            })
            .catch(() => {
            })
    }, [removeOrderAttachment, removePatientAttachment]);

    useEffect(() => {
        getAttachmentList();
    }, [testOrderID, getAttachmentList]);


    const handlePatientAttachmentUpload = useCallback((event: any) => {
        const file = event?.target?.files[0];
        const maxAllowedSize = 5 * 1024 * 1024;
        if (event?.target?.files[0].size > maxAllowedSize) {
            CommonService._alert.showToast('File size should be under 5MB.', "error");
            event.target.value = '';
            return;
        }
        event.target.value = null;
        event.target.files = null;
        setIsContinueVisible(true);
        setShowPatientAttachmentPreviewDialog(true);
        setPatientAttachmentFile(file);
        setNewPatientAttachment(true);
    }, []);

    const handleOrderAttachmentUpload = useCallback((event: any) => {
        const file = event?.target?.files[0];
        const maxAllowedSize = 5 * 1024 * 1024;
        if (event?.target?.files[0].size > maxAllowedSize) {
            CommonService._alert.showToast('File size should be under 5MB.', "error");
            event.target.value = '';
            return;
        }
        event.target.value = null;
        event.target.files = null;
        setIsContinueVisible(true);
        setShowOrderAttachmentPreviewDialog(true);
        setOrderAttachmentFile(file);
        setNewOrderAttachment(true);

    }, []);


    return (
        <div className="test-order-attachment-screen screen t-form-screen">

            <div className="d-flex align-items-center justify-content-space-between">
                <FormTitleWithNavigationBack
                    title={'Order Attachments'}
                    tooltipData={"Post completion of intake form, it will be included under order attachments. Attachments should only be deleted when necessary, as they cannot be retrieved."}
                    handleClick={prev}
                />
                <div className="mrg-right-30 mrg-top-20">
                    {canDiscardTestOrder &&
                        <ButtonComponent
                            startIcon={<DeleteIcon/>}
                            variant={"outlined"}
                            color={'primary'}
                            fullWidth={false}
                            handleClick={onDelete}
                            isLoading={isDeleteLoading}
                        >Discard Test Order</ButtonComponent>
                    }
                </div>
            </div>

            <div className="t-form attachments-with-title-wrapper t-form-back-navigation">
                <form>
                    <div className="t-form-controls-wrapper">
                        <div className="attachment-section-header">Order Attachments</div>
                        <div className="attachment-input-wrapper">
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={12}
                                      md={8}
                                      xl={8}>
                                    <div className={'attachment-input-section'}>
                                        <InputComponent
                                            type={"text"}
                                            name={"title"}
                                            label={"Attachment Name"}
                                            placeholder={"Enter Attachment Name"}
                                            value={orderAttachmentTitle}
                                            onChange={(value: any) => {
                                                setOrderAttachmentTitle(value.replace(/\s/g, ''));
                                            }}
                                        />
                                        {
                                            orderAttachmentTitle.length > 100 &&
                                            <ErrorComponent
                                                errorText={'Attachment name should be less than 100 characters'}/>
                                        }
                                    </div>
                                    <input type={"file"}
                                           className={"display-none"}
                                           ref={orderAttachmentFileRef}
                                           accept={"image/jpeg,image/gif,image/png,application/pdf,image/x-eps"}
                                           onChange={(event) => {
                                               handleOrderAttachmentUpload(event);
                                           }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12}
                                      md={4}
                                      xl={4}>
                                    <ButtonComponent
                                        className="mrg-left-20"
                                        disabled={orderAttachmentTitle?.length === 0 || orderAttachmentTitle?.length > 100}
                                        color={"secondary"}
                                        variant={"outlined"}
                                        fullWidth={false}
                                        size={'large'}
                                        handleClick={() => {
                                            orderAttachmentFileRef.current?.click()
                                        }}>
                                        Choose
                                    </ButtonComponent>
                                </Grid>
                            </Grid>
                        </div>

                        <div className="attachment-list-section">
                            {isAttachmentListLoading &&
                                <LoaderComponent/>
                            }
                            {isAttachmentListLoadingFailed &&
                                <StatusCardComponent title={'failed to load attachment list'}/>
                            }
                            {isAttachmentListLoaded && <>
                                {orderAttachmentList?.length > 0 &&
                                    <div className="attachment-list-wrapper">
                                        {orderAttachmentList?.map((attachment: any) =>
                                            <div key={CommonService.getUUID()}>
                                                <AttachmentViewDownloadComponent
                                                    attachment={attachment}
                                                    attachmentTitle={attachment?.title}
                                                    showDeleteOption={true}
                                                    onClick={(id: any) => handleAttachmentRemove(id, 'order')}
                                                />
                                            </div>
                                        )}
                                    </div>
                                }
                                {orderAttachmentList?.length === 0 &&
                                    <FeedbackComponent className={'width-50'}>No attachment have been
                                        uploaded.</FeedbackComponent>
                                }
                            </>
                            }
                        </div>


                        <div className="attachment-section-header mrg-top-90">Patient Attachments:</div>
                        <div className="attachment-input-wrapper">
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={12}
                                      md={8}
                                      xl={8}>
                                    <div className={'attachment-input-section'}>
                                        <InputComponent
                                            type={"text"}
                                            name={"title"}
                                            label={"Attachment Name"}
                                            placeholder={"Enter Attachment Name"}
                                            value={patientAttachmentTitle}
                                            onChange={(value: any) => {
                                                setPatientAttachmentTitle(value.replace(/\s/g, ''));
                                            }}
                                        />
                                        {
                                            patientAttachmentTitle.length > 100 &&
                                            <ErrorComponent
                                                errorText={'Attachment name should be less than 100 characters'}/>
                                        }
                                    </div>
                                    <input type={"file"}
                                           className={"display-none"}
                                           ref={patientAttachmentFileRef}
                                           accept={"application/pdf,image/jpg, image/jpeg, image/png"}
                                           onChange={(event) => {
                                               handlePatientAttachmentUpload(event);
                                           }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12}
                                      md={4}
                                      xl={4}>
                                    <ButtonComponent
                                        className="mrg-left-20"
                                        disabled={patientAttachmentTitle?.length === 0 || patientAttachmentTitle?.length > 100}
                                        color={"secondary"}
                                        variant={"outlined"}
                                        fullWidth={false}
                                        size={'large'}
                                        handleClick={() => {
                                            patientAttachmentFileRef.current?.click()
                                        }}>
                                        Choose
                                    </ButtonComponent>
                                </Grid>
                            </Grid>
                        </div>

                        <div className="attachment-list-section">
                            {patientAttachmentList?.length > 0 &&
                                <div className="attachment-list-wrapper">
                                    {patientAttachmentList?.map((attachment: any) =>
                                        <div key={CommonService.getUUID()} className={"attachment-list-item"}>
                                            <AttachmentViewDownloadComponent
                                                attachment={attachment}
                                                attachmentTitle={attachment?.title}
                                                showDeleteOption={true}
                                                onClick={(id: any) => handleAttachmentRemove(id, 'patient')}
                                            />
                                        </div>
                                    )}
                                </div>
                            }
                            {patientAttachmentList?.length === 0 &&
                                <FeedbackComponent className={'width-50'}>No attachment have been
                                    uploaded.</FeedbackComponent>
                            }
                            {isAttachmentListLoading &&
                                <LoaderComponent/>
                            }
                        </div>
                    </div>

                    <div className='form-action-section'>
                        <ButtonComponent
                            type="button"
                            color={"primary"}
                            variant={"outlined"}
                            fullWidth={true}
                            handleClick={cancel}
                        >
                            Close
                        </ButtonComponent>
                        <ButtonComponent
                            type="button"
                            color={"primary"}
                            variant={"contained"}
                            fullWidth={true}
                            handleClick={next}
                        >
                            Next
                        </ButtonComponent>
                    </div>
                </form>
            </div>

            <DialogComponent open={showPatientAttachmentPreviewDialog}
                             maxWidth={'md'}
                             title={patientAttachmentTitle}
                             isShowCancel={true}
                             onClose={() => {
                                 setShowPatientAttachmentPreviewDialog(false);
                                 setPatientAttachmentTitle("");
                             }}
                             className={"attachment-preview-dialog"}>
                <div className="attachment-preview-dialog-body" id="file_preview_component">
                    {patientAttachmentFile && (patientAttachmentFile?.type === 'application/pdf' ?
                            <PdfPreviewComponent
                                attachmentFile={patientAttachmentFile}
                                width="550"
                                height="500"
                                isFile={isNewPatientAttachment}
                            />
                            :
                            <FilePreviewerComponent
                                attachmentFile={patientAttachmentFile}
                                attachmentUrl={patientAttachmentFile}
                                isFile={isNewPatientAttachment}/>
                    )
                    }
                </div>
                {patientAttachmentFile && <div className="uploaded-attachment-file-name">
                    <span className="font-weight-bold"> Uploaded File Name:</span>&nbsp;
                    <span>{patientAttachmentFile?.name}</span>
                </div>
                }
                <div className="attachment-preview-dialog-footer">
                    <ButtonComponent variant={"outlined"}
                                     id="cancel_btn"
                                     fullWidth={true}
                                     disabled={isPatientAttachmentBeingUploaded}
                                     handleClick={() => {
                                         patientAttachmentFileRef.current?.click()
                                     }}
                    >
                        Choose Again
                    </ButtonComponent>&nbsp;
                    {
                        isContinueVisible &&
                        <ButtonComponent variant={"contained"}
                                         id="continue_btn"
                                         fullWidth={true}
                                         isLoading={isPatientAttachmentBeingUploaded}
                                         handleClick={onPatientAttachmentSubmit}>
                            Confirm Upload
                        </ButtonComponent>
                    }
                </div>
            </DialogComponent>

            <DialogComponent open={showOrderAttachmentPreviewDialog}
                             maxWidth={'md'}
                             title={orderAttachmentTitle}
                             isShowCancel={true}
                             onClose={() => {
                                 setOrderAttachmentTitle("");
                                 setShowOrderAttachmentPreviewDialog(false)
                             }}
                             className={"attachment-preview-dialog"}>
                <div className="attachment-preview-dialog-body" id="file_preview_component">
                    {orderAttachmentFile && (orderAttachmentFile?.type === 'application/pdf' ?
                            <PdfPreviewComponent
                                attachmentFile={orderAttachmentFile}
                                width="550"
                                height="500"
                                isFile={isNewOrderAttachment}
                            />
                            :
                            <FilePreviewerComponent
                                attachmentFile={orderAttachmentFile}
                                isFile={isNewOrderAttachment}/>
                    )
                    }

                </div>
                {orderAttachmentFile && <div className="uploaded-attachment-file-name">
                    <span className="font-weight-bold"> Uploaded File Name:</span>&nbsp;
                    <span>{orderAttachmentFile?.name}</span>
                </div>
                }
                <div className="attachment-preview-dialog-footer">
                    <ButtonComponent variant={"outlined"}
                                     id="cancel_btn"
                                     fullWidth={true}
                                     disabled={isOrderAttachmentBeingUploaded}
                                     handleClick={() => {
                                         orderAttachmentFileRef.current?.click()
                                     }}
                    >
                        Choose Again
                    </ButtonComponent>&nbsp;
                    {
                        isContinueVisible &&
                        <ButtonComponent variant={"contained"}
                                         id="continue_btn"
                                         fullWidth={true}
                                         isLoading={isOrderAttachmentBeingUploaded}
                                         handleClick={onOrderAttachmentSubmit}>
                            Confirm Upload
                        </ButtonComponent>
                    }
                </div>
            </DialogComponent>
        </div>
    )
};

export default TestOrderAttachmentComponent;
